// src/pages/NewShop.tsx
import React, { useState, ChangeEvent, FormEvent, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    Loader2,
    ArrowLeft,
    Store,
    Mail,
    Phone,
    Hash,
    MapPin,
    Building2,
    Globe2,
    Check,
    ArrowRight
} from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";

import { useAuth } from "@/contexts/AuthContext";
import AuthBranding from "@/components/auth/AuthBranding";

import GoogleAddressAutocomplete from "@/components/ui/GoogleAddressAutocomplete";
import CountryPhoneInput from "@/components/ui/CountryPhoneInput";

import { DEFAULT_SCHEDULE } from "@/lib/constants";
import {
    validateEmail,
    validatePhone,
    validateVatId,
    validateItalianAddress,
    validateItalianZip,
    validateShopName,
    validateName,
} from "@/lib/validator";

type Step = 1 | 2;

interface NewShopFormData {
    shopName: string;
    shopEmail: string;
    address: string;
    city: string;
    zip: string;
    phone: string;
    vatId: string;
    country: string;
    lat?: number;
    long?: number;
}

const NewShop: React.FC = () => {
    const { t } = useTranslation();
    const { completeRegistration, user, loading, logout } = useAuth();
    const navigate = useNavigate();

    const [currentStep, setCurrentStep] = useState<Step>(1);
    const [formData, setFormData] = useState<NewShopFormData>({
        shopName: "",
        shopEmail: "",
        address: "",
        city: "",
        zip: "",
        phone: "",
        vatId: "",
        country: "IT",
        lat: undefined,
        long: undefined,
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleNextStep = (e: FormEvent) => {
        e.preventDefault();

        const { shopName, shopEmail, phone, vatId } = formData;
        if (!shopName || !shopEmail || !phone || !vatId) { toast.error(t("new_shop.validation.fill_all")); return; }
        if (!validateShopName(shopName).isValid) { toast.error("Nome salone non valido"); return; }
        if (!validateEmail(shopEmail).isValid) { toast.error("Email non valida"); return; }
        if (!validatePhone(phone).isValid) { toast.error("Telefono non valido"); return; }
        if (!validateVatId(vatId).isValid) { toast.error("Partita IVA non valida"); return; }
        setCurrentStep(2);
    };

    const handlePrevStep = () => {
        if (currentStep === 2) setCurrentStep(1);
    };

    const handleManualAddressChange = (value: string) => {
        setFormData((prev) => ({
            ...prev,
            address: value,
            lat: undefined,
            long: undefined,
            // city: "",
            //  zip: ""
        }));
    };

    // FUNZIONE PER GESTIRE LA SELEZIONE DI GOOGLE
    const handleAddressSelect = (data: { address: string; city: string; zip: string; country: string; lat: number; long: number }) => {
        setFormData((prev) => ({
            ...prev,
            address: data.address,
            city: data.city,
            zip: data.zip,
            // country: data.country || "IT",
            lat: data.lat,
            long: data.long
        }));
    };

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        if (!user) { toast.error("Errore auth"); return; }
        if (!formData.city.trim() || !formData.address.trim() || !formData.zip.trim()) { toast.error(t("new_shop.validation.fill_all")); return; }

        // --- VALIDAZIONE GOOGLE PLACES ---
        if (formData.address && (!formData.lat || !formData.long)) {
            toast.error("Seleziona un indirizzo valido dal menu a tendina");
            return;
        }

        // Se l'indirizzo è proprio vuoto
        if (!formData.address.trim()) {
            toast.error(t("new_shop.validation.fill_all"));
            return;
        }

        setIsSubmitting(true);
        try {
            const fullName = (user?.user_metadata?.full_name as string | undefined) ?? "";
            const [firstName, ...rest] = fullName.split(" ");
            const lastName = rest.join(" ");
            const shopData = {
                first_name: firstName || "",
                last_name: lastName || "",
                email: user?.email,
                role: "admin",
                is_active: true,
                time_ranges: DEFAULT_SCHEDULE,
                shop_name: formData.shopName,
                address: formData.address,
                city: formData.city,
                zip: formData.zip,
                vat_id: formData.vatId,
                phone: formData.phone,
                shop_email: formData.shopEmail,
                lat: formData.lat,
                long: formData.long,
            };

            const result = await completeRegistration(shopData);
            if (!result.success) {
                toast.error(result.error || t("new_shop.notifications.creation_error"));
            } else {
                toast.success(t("new_shop.notifications.success"));
                setTimeout(() => {
                    navigate("/subscription", { replace: true });
                }, 300); // piccolo delay necessario
            }
        } catch (err) {
            console.error(err);
            toast.error(t("new_shop.notifications.creation_error"));
        } finally {
            setIsSubmitting(false);
        }
    };

    const stepVariants = {
        enter: (direction: number) => ({ x: direction > 0 ? 20 : -20, opacity: 0 }),
        center: { x: 0, opacity: 1 },
        exit: (direction: number) => ({ x: direction < 0 ? 20 : -20, opacity: 0 }),
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <Loader2 className="h-8 w-8 animate-spin text-clip-primary" />
            </div>
        );
    }

    return (
        <div className="min-h-screen w-full flex flex-col lg:flex-row bg-white font-sans overflow-x-hidden">

            {/* RIUTILIZZO AUTH BRANDING IN MODALITÀ CUSTOM */}
            <AuthBranding
                view="custom"
                title={
                    <>
                        {t("new_shop.hero.title", "Configura il tuo")}{" "}
                        <span className="text-clip-500">Salone</span>
                    </>
                }
                subtitle={
                    <>
                        {t("new_shop.hero.subtitle", "Sei a un passo dal trasformare la gestione del tuo lavoro.")}
                    </>
                }
                footerText="Vuoi cambiare account?"
                footerAction={{
                    label: "Logout",
                    onClick: () => logout().then(() => window.location.href = "/login")
                }}
                onLogoClick={() => navigate("/subscription")}
            />

            {/* --- RIGHT PANEL --- */}
            <div className="flex-1 flex flex-col justify-center items-center bg-white p-6 sm:p-12 lg:p-20 relative z-10">
                <div className="max-w-md w-full mx-auto">

                    {/* Header Testuale */}
                    <div className="mb-10">
                        {currentStep === 2 && (
                            <button
                                onClick={handlePrevStep}
                                className="mb-6 flex items-center gap-2 text-sm font-bold text-gray-500 hover:text-gray-900 transition-colors"
                            >
                                <ArrowLeft size={16} /> Indietro
                            </button>
                        )}
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2 tracking-tight">
                            {currentStep === 1 ? t("new_shop.form.title", "Dati del Salone") : "Indirizzo e Sede"}
                        </h2>
                        <p className="text-gray-500">
                            {currentStep === 1
                                ? "Inserisci le informazioni principali della tua attività."
                                : "Dove si trova il tuo salone?"
                            }
                        </p>
                    </div>

                    {/* FORM */}
                    <form onSubmit={currentStep === 2 ? handleSubmit : handleNextStep} className="space-y-5">
                        <AnimatePresence mode="wait" custom={currentStep}>
                            {currentStep === 1 ? (
                                <motion.div
                                    key="step1"
                                    custom={1}
                                    variants={stepVariants}
                                    initial="enter"
                                    animate="center"
                                    exit="exit"
                                    transition={{ duration: 0.3 }}
                                    className="space-y-5"
                                >
                                    {/* INPUT STEP 1 */}
                                    <div className="space-y-1.5">
                                        <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1">Nome Salone</label>
                                        <div className="relative group">
                                            <Store className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-clip-500 transition-colors" size={18} />
                                            <input type="text" name="shopName" value={formData.shopName} onChange={handleInputChange} className="w-full pl-11 pr-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl focus:border-clip-500 focus:ring-2 focus:ring-clip-200 outline-none transition-all font-medium text-gray-900 placeholder:text-gray-400" placeholder="Clip Barber Studio" autoFocus />
                                        </div>
                                    </div>
                                    <div className="space-y-1.5">
                                        <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1">Email Salone</label>
                                        <div className="relative group">
                                            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-clip-500 transition-colors" size={18} />
                                            <input type="email" name="shopEmail" value={formData.shopEmail} onChange={handleInputChange} className="w-full pl-11 pr-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl focus:border-clip-500 focus:ring-2 focus:ring-clip-200 outline-none transition-all font-medium text-gray-900 placeholder:text-gray-400" placeholder="info@tuosalone.it" />
                                        </div>
                                    </div>
                                    <div className="space-y-1.5">
                                        <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1">Telefono</label>
                                        <div className="relative group">
                                            <CountryPhoneInput
                                                value={formData.phone}
                                                onChange={(val) => setFormData(prev => ({ ...prev, phone: val }))}
                                                placeholder="333 123 4567"
                                            />
                                        </div>
                                    </div>
                                    <div className="space-y-1.5">
                                        <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1">Partita IVA</label>
                                        <div className="relative group">
                                            <Hash className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-clip-500 transition-colors" size={18} />
                                            <input type="text" name="vatId" value={formData.vatId} onChange={handleInputChange} className="w-full pl-11 pr-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl focus:border-clip-500 focus:ring-2 focus:ring-clip-200 outline-none transition-all font-medium text-gray-900 placeholder:text-gray-400" placeholder="IT12345678901" />
                                        </div>
                                    </div>
                                </motion.div>
                            ) : (
                                <motion.div
                                    key="step2"
                                    custom={-1}
                                    variants={stepVariants}
                                    initial="enter"
                                    animate="center"
                                    exit="exit"
                                    transition={{ duration: 0.3 }}
                                    className="space-y-5"
                                >
                                    {/* INPUT STEP 2 */}
                                    <div className="space-y-1.5">
                                        <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1">Indirizzo</label>

                                        <GoogleAddressAutocomplete
                                            onAddressSelect={handleAddressSelect}
                                            onInputChange={handleManualAddressChange}
                                            defaultValue={formData.address}
                                        />
                                    </div>

                                    <div className="flex gap-4">
                                        <div className="flex-1 space-y-1.5">
                                            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1">Città</label>
                                            <div className="relative group">
                                                <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 transition-colors" size={18} />
                                                <input
                                                    type="text"
                                                    name="city"
                                                    value={formData.city}
                                                    onChange={handleInputChange}
                                                    readOnly
                                                    disabled
                                                    className="w-full pl-11 pr-4 py-3.5 bg-gray-100 border border-gray-200 rounded-xl outline-none transition-all font-medium text-gray-500 placeholder:text-gray-400 cursor-not-allowed"
                                                    placeholder="Compilazione automatica..."
                                                />
                                            </div>
                                        </div>
                                        <div className="w-32 space-y-1.5">
                                            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1">CAP</label>
                                            <div className="relative group">
                                                <Hash className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 transition-colors" size={18} />
                                                <input
                                                    type="text"
                                                    name="zip"
                                                    value={formData.zip}
                                                    onChange={handleInputChange}
                                                    readOnly
                                                    disabled
                                                    className="w-full pl-11 pr-4 py-3.5 bg-gray-100 border border-gray-200 rounded-xl outline-none transition-all font-medium text-gray-500 placeholder:text-gray-400 cursor-not-allowed"
                                                    placeholder="Auto..."
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="space-y-1.5">
                                        <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1">Paese</label>
                                        <div className="relative group">
                                            <Globe2 className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                                            <input type="text" name="country" value={formData.country} readOnly disabled className="w-full pl-11 pr-4 py-3.5 bg-gray-100 border border-gray-200 rounded-xl text-gray-500 cursor-not-allowed font-medium" />
                                        </div>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        <div className="pt-2">
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className={`w-full py-4 text-white font-bold rounded-xl shadow-lg transition-all active:scale-[0.98] flex items-center justify-center gap-2 mt-4
                                    ${isSubmitting
                                        ? "bg-gray-300 cursor-not-allowed shadow-none"
                                        : "bg-gray-900 hover:bg-black hover:shadow-gray-300/50"
                                    }
                                `}
                            >
                                {isSubmitting ? (
                                    <Loader2 size={20} className="animate-spin" />
                                ) : currentStep === 2 ? (
                                    <>
                                        {t("new_shop.form.create_btn", "Crea Salone")}
                                        <Check size={18} />
                                    </>
                                ) : (
                                    <>
                                        {t("new_shop.form.continue_btn", "Continua")}
                                        <ArrowRight size={18} />
                                    </>
                                )}
                            </button>
                        </div>
                    </form>

                    {/* Mobile Footer */}
                    <div className="lg:hidden mt-8 text-center">
                        <button onClick={() => logout().then(() => window.location.href = "/login")} className="text-sm text-gray-500 hover:text-gray-900 transition-colors underline">
                            Logout
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default NewShop;